/**
 * THE GRID FAILS, AND THE BACKUP TAKES OVER: what is laid over the sign-in's photograph of a data
 * centre's rack aisle (`SitePhoto.tsx`), Hamzah's pick on 2026-10-20 ("Grid fails, backup takes over").
 *
 * The photograph (generated, see `signin.css`) looks down a cold aisle between two rows of racks to a
 * glass wall at its end, and through it a standby set on the grass of a green field. Every overlay is
 * on a real object in it and nothing glows where the picture has no lamp (the rule the bank scene
 * learned, 2026-10-16): the ceiling's own light panels, the racks' own status lamps, the set's own
 * roof caps, and the photograph's own lit pixels.
 *
 * ── ONE STORY A MINUTE, the one the product watches (Grid continuity, Generator readiness) ──────
 *     0-62 %   on the grid: the ceiling lit, the rack lamps blinking, each to its own beat
 *       62 %   the grid is lost: the ceiling panels go out and the room drops into shade, and the
 *              racks KEEP their light and keep blinking, because the batteries carry them. That is
 *              the point of the room, so it is the one moment this scene is built around
 *    66-87 %   the standby set outside starts: smoke from its roof caps; the lights come back at 67 %
 *       87 %   the grid returns: the set runs on and cools down, its smoke thinning out
 * Timings in `signin.css`; the words for each moment are `PowerRoomCaption`. No battery cabinet is
 * in the frame, so none is lit: the caption says the batteries keep the racks running.
 *
 * WHAT STAYS LIT IS THE PHOTOGRAPH ITSELF. The shade covers the whole room, and above it, while the
 * ceiling is out, sits `/signin-site-glow.webp`: the photograph's own emitting pixels (the racks'
 * green and blue lamps and light bars, and the daylight through the glass) cut out of it with their
 * glow, at 1.5x this scene's size. A drawn lamp would sit near the real one; this is the real one.
 * The blinking is a dark cover on 102 of the small lamps, chosen across the whole frame, each on its
 * own seeded period and phase, about a third fast (activity) and the rest slow: never a wave.
 *
 * ── ONE COORDINATE SPACE ───────────────────────────────────────────────────────────────────────
 * Everything is in the generated frame's pixels, 1344x768 (the photograph is that frame upscaled
 * 4x). The lamps and panels were found in it by colour and brightness, the window and the roof caps
 * read off it with a grid. The page shows a crop of it (`--crop-x`, `--crop-w` in `signin.css`),
 * and the <svg> is sized and shifted by the crop, so a lamp stays on its lamp in both crops.
 *
 * Pure geometry, no state: `aria-hidden` with the rest of the world, it carries mood, and the
 * caption says the same thing in words for anyone who reads it.
 */
import type { ReactNode } from 'react';

const W = 1344;
const H = 768;

/* The glass wall at the aisle's end: the daylight in it is not on the room's supply. */
const OPENING = { x0: 577, y0: 382, x1: 721, y1: 505 };
/* The standby set's two roof caps, where its smoke leaves. */
const CAPS: ReadonlyArray<readonly [number, number]> = [
  [634, 412],
  [668, 412],
];
/* The ceiling's lit panels, as found: x0, y0, x1, y1. */
const PANELS: ReadonlyArray<readonly [number, number, number, number]> = [
  [348, 0, 432, 28], [806, 0, 885, 29], [767, 35, 867, 138], [370, 36, 484, 139], [435, 144, 519, 212],
  [738, 144, 813, 211], [725, 215, 777, 252], [482, 216, 537, 253], [713, 256, 754, 283],
  [505, 257, 551, 284], [524, 288, 556, 301], [701, 288, 738, 314], [537, 307, 564, 315],
  [545, 318, 571, 329], [696, 319, 723, 329], [550, 331, 578, 343], [691, 331, 716, 342],
  [689, 344, 709, 352], [559, 345, 582, 354], [683, 357, 704, 366], [567, 359, 588, 368],
  [667, 370, 704, 381],
];
/* The rack lamps that blink: x, y, radius, fast (1) or slow (0), period s, phase s (negative). */
const LEDS: ReadonlyArray<readonly [number, number, number, number, number, number]> = [
  [11, 326, 1.6, 1, 0.78, -0.48], [13, 322, 1.6, 0, 4.82, -0.6], [26.5, 326, 1.6, 0, 4.01, -1.83],
  [33, 739.5, 1.6, 1, 0.79, -0.44], [43.5, 516, 1.6, 1, 0.94, -0.07], [47.7, 520.7, 1.6, 0, 3.7, -3.31],
  [73.5, 339, 1.6, 1, 1.01, -0.45], [81, 713.5, 1.6, 0, 3.67, -3.08], [95.5, 444, 1.6, 0, 3.48, -3.46],
  [102.5, 446, 1.6, 0, 4.36, -3.36], [103.2, 42.8, 1.6, 0, 5.89, -4.41], [117, 352, 1.6, 0, 2.91, -2.83],
  [168.9, 622.4, 2, 1, 1.07, -0.69], [178.5, 372, 1.6, 0, 2.81, -0.16], [183.5, 641, 1.6, 0, 3.18, -2.43],
  [195.5, 636, 1.6, 1, 0.7, -0.15], [199.5, 636, 1.6, 1, 0.78, -0.27], [219.5, 511, 1.6, 1, 0.72, -0.57],
  [226.1, 647.3, 2.5, 1, 0.8, -0.11], [232, 646, 1.6, 0, 3.16, -2.17], [275.2, 675.8, 1.6, 1, 1.02, -0.72],
  [276.5, 276, 1.6, 0, 5.34, -1.02], [284, 668.5, 1.6, 1, 0.91, -0.29], [290.4, 452.2, 2, 1, 0.58, -0.45],
  [293, 396.5, 1.6, 1, 0.55, -0.54], [305.5, 458, 1.6, 0, 2.26, -0.19], [338.8, 634.8, 2, 1, 1, -0.06],
  [343.8, 508.1, 2.5, 0, 5.86, -4.19], [344.5, 328, 1.6, 0, 6.35, -2.5], [358, 455.5, 1.6, 0, 2.51, -2.4],
  [383, 605.5, 1.6, 0, 3.09, -1.12], [383.5, 572, 1.6, 0, 3.2, -2.93], [384.8, 595.2, 1.6, 1, 0.69, -0.1],
  [385.5, 604, 1.6, 0, 5.26, -4.06], [386.6, 394.8, 1.6, 1, 0.75, -0.65],
  [419, 388.5, 1.6, 1, 0.67, -0.45], [442.3, 393.2, 2, 1, 1, -0.06], [443.4, 342.4, 2.5, 1, 0.92, -0.48],
  [449, 375.5, 1.6, 1, 0.95, -0.27], [463, 432.5, 1.6, 0, 5.94, -5.4], [464, 493, 1.6, 0, 6.26, -5.86],
  [481.5, 404, 1.6, 0, 3.99, -3.98], [509.9, 487.7, 1.6, 0, 5.71, -0.88],
  [510.5, 484.5, 1.6, 0, 4.09, -3.76], [523.4, 453.3, 2, 0, 4.07, -0.41], [539, 455, 2.5, 1, 0.71, -0.2],
  [541, 471.5, 1.6, 0, 3.18, -2.33], [553, 458.5, 1.6, 0, 4.74, -3.26], [563.6, 460.3, 2.5, 0, 4, -3.95],
  [732.7, 463.2, 2, 1, 0.84, -0.16], [756, 490.5, 2, 0, 5.94, -4.87], [758, 443.5, 1.6, 1, 0.48, -0.41],
  [767, 508.5, 1.6, 1, 0.45, -0.4], [792, 437.5, 2, 0, 4.41, -2.35], [797, 393, 1.6, 0, 5.51, -0.18],
  [798, 507.5, 1.6, 1, 1.01, -0.75], [810, 380.5, 2, 1, 0.69, -0.33], [813.7, 429.7, 1.6, 1, 0.5, -0.34],
  [830.2, 412.8, 1.6, 1, 0.76, -0.38], [841, 433.5, 1.6, 0, 3.1, -0.8], [849, 437, 1.6, 1, 0.7, -0.61],
  [852, 338.5, 2, 0, 5.81, -1.4], [874, 309.7, 2.5, 0, 3.17, -2.04], [875, 446.5, 2, 0, 4.26, -0.19],
  [878.5, 445, 2.5, 0, 4.55, -3.1], [907, 383, 1.6, 1, 1.02, -0.6], [907.1, 560, 2.5, 0, 6.06, -5.79],
  [914, 422.5, 1.6, 1, 1.07, -0.2], [933, 401, 1.6, 0, 2.61, -1.1], [942, 499.5, 2, 1, 1.04, -0.54],
  [952.3, 315.8, 2, 1, 0.47, -0.05], [960, 353.5, 1.6, 0, 3.01, -2.54], [960, 390, 1.6, 1, 0.67, -0.64],
  [968, 465.5, 2, 0, 2.83, -2.04], [992.4, 300.8, 1.6, 0, 4.4, -3.05], [995.5, 346, 1.6, 0, 5.06, -2.88],
  [1000.8, 512.8, 2.5, 0, 4.89, -2.03], [1046.5, 512, 1.6, 1, 0.52, -0.28],
  [1047, 699.5, 1.6, 0, 6.11, -5.81], [1053.4, 398.4, 2, 0, 6.17, -3.05],
  [1081, 516.5, 1.6, 0, 2.57, -1.89], [1084, 646.5, 1.6, 1, 1.09, -0.47],
  [1088.5, 450.5, 1.6, 1, 0.56, -0.03], [1092, 209.5, 1.6, 1, 0.93, -0.85],
  [1107, 511.9, 2.5, 0, 2.37, -0.59], [1147, 375, 1.6, 0, 4.93, -1.41], [1150.5, 217, 1.6, 1, 0.91, -0.09],
  [1151.4, 741.6, 2, 0, 6, -5.81], [1168.2, 753.8, 1.6, 1, 0.85, -0.8], [1168.5, 220, 1.6, 0, 5.96, -4.95],
  [1172, 460.5, 2, 0, 3.88, -2.8], [1245, 148.5, 1.6, 0, 4.18, -2.67], [1245.2, 341.7, 2, 0, 2.71, -0.21],
  [1253, 198, 2.5, 1, 0.98, -0.11], [1270.7, 629.9, 1.6, 1, 0.83, -0.62],
  [1274.7, 450.7, 2, 1, 0.5, -0.46], [1307.5, 231, 1.6, 1, 0.64, -0.64], [1311, 29.5, 1.6, 0, 5.43, -1.81],
  [1311.5, 229, 1.6, 0, 3, -2.45], [1312.5, 722, 1.6, 0, 2.88, -0.85], [1314.5, 27, 1.6, 1, 1.06, -0.56],
  [1325.3, 638.3, 1.6, 0, 5.37, -4],
];

export function PowerRoomScene(): ReactNode {
  return (
    <svg className="lm-si-pr" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden focusable="false">
      <defs>
        <filter id="lm-si-pr-soft" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
        <filter id="lm-si-pr-haze" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>
        <clipPath id="lm-si-pr-view">
          <rect x={OPENING.x0} y={OPENING.y0} width={OPENING.x1 - OPENING.x0} height={OPENING.y1 - OPENING.y0} />
        </clipPath>
      </defs>

      {/* The ceiling's panels, switched off while the room has no supply but its batteries. */}
      <g className="lm-si-pr-panels" filter="url(#lm-si-pr-soft)">
        {PANELS.map(([x0, y0, x1, y1]) => (
          <rect key={`${x0}-${y0}`} x={x0 - 2} y={y0 - 2} width={x1 - x0 + 4} height={y1 - y0 + 4} rx="2" className="lm-si-pr-panel" />
        ))}
      </g>
      {/* The room in shade, and over it what keeps its light: the racks' own lamps and the day. */}
      <rect className="lm-si-pr-dim" x="0" y="0" width={W} height={H} />
      <image className="lm-si-pr-glow" href="/signin-site-glow.webp" x="0" y="0" width={W} height={H} preserveAspectRatio="none" />

      {/* The rack lamps' own blinking, on through the outage: a dark cover over the lamp, briefly. */}
      <g className="lm-si-pr-leds">
        {LEDS.map(([x, y, r, fast, period, phase]) => (
          <circle
            key={`${x}-${y}`}
            cx={x}
            cy={y}
            r={r}
            className={fast ? 'lm-si-pr-led is-fast' : 'lm-si-pr-led'}
            style={{ animationDuration: `${period}s`, animationDelay: `${phase}s` }}
          />
        ))}
      </g>

      {/* The standby set running: smoke from its roof caps, seen only through the glass. */}
      <g clipPath="url(#lm-si-pr-view)">
        <g className="lm-si-pr-smoke" filter="url(#lm-si-pr-haze)">
          {CAPS.flatMap(([x, y], c) =>
            [0, 1, 2].map((i) => (
              <circle
                key={`${c}-${i}`}
                cx={x}
                cy={y}
                r="3.2"
                className="lm-si-pr-puff"
                style={{ animationDelay: `${(i * 0.9 + c * 0.45).toFixed(2)}s`, ['--drift' as string]: `${4 + i * 2 - c}px` }}
              />
            )),
          )}
        </g>
      </g>
    </svg>
  );
}

/**
 * The story in words, one line at a time, on the same minute as the scene. Visible text rather than
 * `aria-live`: it changes every few seconds and an announcement would interrupt someone signing in.
 */
export function PowerRoomCaption(): ReactNode {
  return (
    <p className="lm-si-pr-caption" aria-hidden>
      <span className="lm-si-pr-cap lm-si-pr-cap-grid">
        <i className="is-grid" />
        On the grid
      </span>
      <span className="lm-si-pr-cap lm-si-pr-cap-lost">
        <i className="is-teal" />
        Grid lost: the batteries keep the racks running
      </span>
      <span className="lm-si-pr-cap lm-si-pr-cap-gen">
        <i className="is-gen" />
        Generator started: carrying the room
      </span>
      <span className="lm-si-pr-cap lm-si-pr-cap-back">
        <i className="is-grid" />
        Grid back: the generator cools down
      </span>
    </p>
  );
}
